import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Upload, Download, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

export const Base64Tool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');

  const encodeBase64 = (text: string) => {
    try {
      const encoded = btoa(unescape(encodeURIComponent(text)));
      setOutput(encoded);
      setError('');
      return encoded;
    } catch (err) {
      const errorMessage = t('tools.base64.failedToEncode');
      setError(errorMessage);
      setOutput('');
      return null;
    }
  };

  const decodeBase64 = (base64: string) => {
    try {
      const decoded = decodeURIComponent(escape(atob(base64)));
      setOutput(decoded);
      setError('');
      return decoded;
    } catch (err) {
      const errorMessage = t('tools.base64.invalidBase64');
      setError(errorMessage);
      setOutput('');
      return null;
    }
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setError('');
    
    if (value.trim()) {
      if (mode === 'encode') {
        encodeBase64(value);
      } else {
        decodeBase64(value);
      }
    } else {
      setOutput('');
    }
  };

  const switchMode = () => {
    const newMode = mode === 'encode' ? 'decode' : 'encode';
    setMode(newMode);
    
    // Swap input and output
    const tempInput = input;
    setInput(output);
    setOutput(tempInput);
    
    // Process the swapped input
    if (output.trim()) {
      if (newMode === 'encode') {
        encodeBase64(output);
      } else {
        decodeBase64(output);
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadResult = () => {
    if (output) {
      const filename = mode === 'encode' ? t('tools.base64.encodedFilename') : t('tools.base64.decodedFilename');
      const blob = new Blob([output], { type: 'text/plain' });
      saveAs(blob, filename);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        if (mode === 'encode') {
          encodeBase64(content);
        } else {
          decodeBase64(content);
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const isImageBase64 = (base64String: string): boolean => {
    return base64String.startsWith('data:image/') ||
           (mode === 'decode' && !!base64String.match(/^[A-Za-z0-9+/]*={0,2}$/) && base64String.length > 100);
  };

  const getImageDataUrl = (base64String: string): string => {
    if (base64String.startsWith('data:image/')) {
      return base64String;
    }
    // Assume it's a JPEG if no header
    return `data:image/jpeg;base64,${base64String}`;
  };

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.base64.name')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => {
                setMode('encode');
                if (input) encodeBase64(input);
              }}
              variant={mode === 'encode' ? 'primary' : 'secondary'}
            >
              {t('tools.base64.encode')}
            </Button>
            <Button
              onClick={() => {
                setMode('decode');
                if (input) decodeBase64(input);
              }}
              variant={mode === 'decode' ? 'primary' : 'secondary'}
            >
              {t('tools.base64.decode')}
            </Button>
            <Button
              onClick={switchMode}
              variant="outline"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {t('tools.base64.switch')}
            </Button>
            <Button
              onClick={() => output && copyToClipboard(output)}
              disabled={!output}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={downloadResult}
              disabled={!output}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.download')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.json,.xml,.csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {mode === 'encode' ? t('tools.base64.input') : t('tools.base64.base64Input')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={
                mode === 'encode'
                  ? t('tools.base64.enterTextToEncode')
                  : t('tools.base64.enterBase64ToDecode')
              }
              className="min-h-[300px] font-mono text-sm"
            />
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
                <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {mode === 'encode' ? t('tools.base64.output') : t('tools.base64.output')}
              </span>
              {output && (
                <span className="text-sm text-gray-500">
                  {output.length} {t('tools.base64.chars')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder={
                mode === 'encode'
                  ? t('tools.base64.base64OutputPlaceholder')
                  : t('tools.base64.decodedOutputPlaceholder')
              }
              className="min-h-[300px] font-mono text-sm bg-gray-50 dark:bg-gray-800"
            />

            {/* Image Preview */}
            {output && isImageBase64(output) && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.base64.imagePreview')}:
                </h4>
                <div className="border rounded-lg p-4 bg-white dark:bg-gray-800">
                  <img
                    src={getImageDataUrl(output)}
                    alt={t('tools.base64.imagePreviewAlt')}
                    className="max-w-full max-h-64 object-contain mx-auto"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Statistics */}
      {output && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.base64.statistics')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.base64.inputLength')}:</span>
                <div className="font-mono">{input.length.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.base64.outputLength')}:</span>
                <div className="font-mono">{output.length.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.base64.sizeChange')}:</span>
                <div className="font-mono">
                  {mode === 'encode'
                    ? `+${((output.length - input.length) / input.length * 100).toFixed(1)}%`
                    : `${((output.length - input.length) / input.length * 100).toFixed(1)}%`
                  }
                </div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.base64.outputSize')}:</span>
                <div className="font-mono">{(new Blob([output]).size / 1024).toFixed(2)} {t('tools.base64.kb')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
